import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Dashboard.css';

function renderMarkdown(text) {
  if (!text) return "";
  let html = text;

  // Escape HTML entities
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Bold text: **bold**
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Code Blocks: ```lang ... ```
  html = html.replace(/```([\s\S]*?)```/g, (match, codeBlock) => {
    const lines = codeBlock.trim().split('\n');
    let lang = "";
    let code = codeBlock;
    if (lines[0].length < 15 && !lines[0].includes(' ') && lines[0].match(/^[a-zA-Z0-9+#]+$/)) {
      lang = lines[0];
      code = lines.slice(1).join('\n');
    }
    return `<pre><code class="language-${lang}">${code.trim()}</code></pre>`;
  });

  // Inline Code: `code`
  html = html.replace(/`(.*?)`/g, '<code>$1</code>');

  // Bullets: * or -
  html = html.replace(/^\s*[*+-]\s+(.*?)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*?<\/li>)+/g, '<ul>$&</ul>');

  // Format paragraphs
  const sections = html.split('</pre>');
  for (let i = 0; i < sections.length; i++) {
    const parts = sections[i].split('<pre>');
    parts[0] = parts[0]
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => line.startsWith('<ul>') || line.startsWith('<li>') || line.startsWith('</ul>') ? line : `<p>${line}</p>`)
      .join('');
    sections[i] = parts.join('<pre>');
  }
  html = sections.join('</pre>');

  return `<div class="suggestion-content">${html}</div>`;
}

// Ensure screenshot base64 strings have proper URI scheme
function formatImageSrc(imgStr) {
  if (!imgStr || typeof imgStr !== 'string') return '';
  const trimmed = imgStr.trim();
  if (
    trimmed.startsWith('data:image/') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }
  if (trimmed.startsWith('/9j/')) {
    return `data:image/jpeg;base64,${trimmed}`;
  }
  return `data:image/png;base64,${trimmed}`;
}

// Extract all valid screenshots from a transcript entry
function getEntryScreenshots(entry) {
  if (!entry) return [];
  const list = [];
  if (Array.isArray(entry.screenshots) && entry.screenshots.length > 0) {
    entry.screenshots.forEach(s => {
      if (s) list.push(s);
    });
  } else if (typeof entry.screenshots === 'string' && entry.screenshots.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(entry.screenshots);
      if (Array.isArray(parsed)) {
        parsed.forEach(s => { if (s) list.push(s); });
      }
    } catch (e) {}
  }
  if (list.length === 0 && entry.screenshot) {
    list.push(entry.screenshot);
  }
  return list.map(formatImageSrc).filter(Boolean);
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4310';

const Dashboard = ({ 
  user, 
  onLogout, 
  onUpdateUser, 
  activeTab = 'overview', 
  pastInterviews = [], 
  setPastInterviews, 
  onError, 
  onSuccess 
}) => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Navigation & UI States
  const [currentTab, setCurrentTab] = useState(activeTab);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [detailInterview, setDetailInterview] = useState(null);

  // Close Lightbox on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && previewImage) {
        setPreviewImage(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewImage]);

  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem('coprep_theme_mode') || 'dark';
    } catch (e) {
      return 'dark';
    }
  });

  const handleToggleTheme = () => {
    const nextTheme = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(nextTheme);
    try {
      localStorage.setItem('coprep_theme_mode', nextTheme);
    } catch (e) {}
  };

  useEffect(() => {
    document.body.classList.toggle('light-theme', themeMode === 'light');
    document.documentElement.setAttribute('data-theme', themeMode);
    return () => {
      document.body.classList.remove('light-theme');
    };
  }, [themeMode]);
  
  // Accordion Expand/Collapse States in Sidebar
  const [expandedGroups, setExpandedGroups] = useState({
    interview: true,
    resume: false,
    jobsearch: false,
    resources: false
  });

  // Modals
  const [showDesktopModal, setShowDesktopModal] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [copiedTerminal, setCopiedTerminal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Interview Setup Form States
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('mid');
  const [selectedType, setSelectedType] = useState('technical');
  const [selectedModel, setSelectedModel] = useState('gpt4');
  const [resume, setResume] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [launchStatus, setLaunchStatus] = useState('');

  // Pricing / Store States
  const [pricingTiers, setPricingTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState('');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [downloadUrls, setDownloadUrls] = useState({
    windows: 'https://www.dropbox.com/scl/fi/afllqekpp0lwo5a0i23x5/CrackIt-Setup-1.0.0.exe?rlkey=eu2zq43483gnw1ony4nululgw&st=mse3kmm7&dl=1',
    mac: 'https://www.dropbox.com/scl/fi/su8lex4z8wkb0zjqnjbwf/CrackIt-1.0.0-arm64.dmg?rlkey=595ep1wi1dhdvn1jqel01qoa4&st=w2n09ffr&dl=1'
  });

  // Dynamic Models
  const [models, setModels] = useState([
    { 
      id: 'gpt4', 
      name: 'GPT-4o Omniverse', 
      developer: 'Recommended', 
      type: 'chip',
      desc: 'Ultra low-latency streaming code generation and live conversational pacing.' 
    },
    { 
      id: 'gemini', 
      name: 'Gemini 1.5 Pro', 
      developer: 'Coming Soon', 
      type: 'brain',
      desc: 'Highest context window with real-time multimodal screen understanding.' 
    },
    { 
      id: 'claude', 
      name: 'Claude 3.5 Sonnet', 
      developer: 'Coming Soon', 
      type: 'layers',
      desc: 'Deep complex logic reasoning, code synthesis, and architectural design.' 
    }
  ]);

  // Sync currentTab with activeTab prop changes
  useEffect(() => {
    if (activeTab) {
      setCurrentTab(activeTab);
    }
  }, [activeTab]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Fetch AI Models, Pricing, and Download URLs in parallel on mount or tab change
  const fetchDashboardMeta = useCallback(async () => {
    try {
      const [modelsRes, pricingRes, downloadsRes] = await Promise.all([
        fetch(`${API_URL}/api/models`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/pricing`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/downloads`).then(r => r.json()).catch(() => null)
      ]);

      if (modelsRes && modelsRes.success && Array.isArray(modelsRes.models)) {
        setModels(modelsRes.models.map(m => ({ ...m, type: m.id === 'gpt4' ? 'chip' : m.id === 'gemini' ? 'brain' : 'layers' })));
      }

      if (pricingRes && pricingRes.success && Array.isArray(pricingRes.plans)) {
        const sorted = pricingRes.plans.sort((a, b) => a.displayOrder - b.displayOrder);
        setPricingTiers(sorted);
        const popularPlan = sorted.find(p => p.isPopular) || sorted[0];
        if (popularPlan) {
          setSelectedTier(popularPlan._id);
        }
      }

      if (downloadsRes && downloadsRes.success && downloadsRes.downloads) {
        setDownloadUrls(downloadsRes.downloads);
      }
    } catch (err) {
      console.error('Error fetching dashboard meta:', err);
    }
  }, []);

  useEffect(() => {
    fetchDashboardMeta();
  }, [fetchDashboardMeta]);

  // Re-fetch when entering the Store tab
  useEffect(() => {
    if (currentTab === 'store') {
      fetchDashboardMeta();
    }
  }, [currentTab, fetchDashboardMeta]);

  // Razorpay Script Loader
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Razorpay Purchase Handler
  const handlePurchase = async (planId) => {
    if (!planId) {
      if (onError) onError('Please select a pricing plan tier.');
      return;
    }
    
    setSelectedTier(planId);
    setIsPurchasing(true);
    try {
      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        if (onError) onError('Razorpay checkout SDK failed to load. Please check your internet connection.');
        setIsPurchasing(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/payments/razorpay/create-order`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ planId })
      });
      
      const data = await response.json();
      
      if (!data.success || !data.orderId) {
        setIsPurchasing(false);
        if (onError) onError('Failed to initiate order: ' + (data.message || 'Unknown error'));
        return;
      }

      const options = {
        key: data.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_TdtzwCzuRpMxOQ',
        amount: data.amount,
        currency: data.currency || 'INR',
        name: 'CrackIt AI Live Copilot',
        description: `${data.plan.name} Plan (${data.plan.credits} Credits)`,
        image: 'https://cdn-icons-png.flaticon.com/512/4712/4712109.png',
        order_id: data.orderId,
        handler: async function (paymentResponse) {
          try {
            const verifyRes = await fetch(`${API_URL}/api/payments/razorpay/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
                planId: planId
              })
            });

            const verifyData = await verifyRes.json();
            setIsPurchasing(false);

            if (verifyData.success) {
              if (onUpdateUser) {
                onUpdateUser(prev => ({
                  ...prev,
                  credits: verifyData.credits
                }));
              }
              if (onSuccess) {
                onSuccess(`Payment Successful! Added ${Math.round(data.plan.credits).toLocaleString()} credits to your account.`);
              }
              window.dispatchEvent(new CustomEvent('crackit_credits_updated', { detail: { credits: verifyData.credits } }));
            } else {
              if (onError) onError('Payment verification failed: ' + (verifyData.message || 'Signature mismatch'));
            }
          } catch (verifyErr) {
            console.error('Error verifying payment:', verifyErr);
            setIsPurchasing(false);
            if (onError) onError('Payment verified locally, but server sync failed.');
          }
        },
        prefill: {
          name: user ? user.name : '',
          email: user ? user.email : ''
        },
        theme: {
          color: '#1877f2'
        },
        modal: {
          ondismiss: function () {
            setIsPurchasing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (failResponse) {
        console.error('[Razorpay] Payment failed:', failResponse.error);
        if (onError) onError(`Payment failed: ${failResponse.error.description || 'Transaction declined'}`);
        setIsPurchasing(false);
      });
      rzp.open();

    } catch (error) {
      console.error('Error starting Razorpay Checkout:', error);
      if (onError) onError(`Billing connection error. Ensure backend is running at ${API_URL}`);
      setIsPurchasing(false);
    }
  };

  // Launch Session Handler
  const handleLaunch = async (e) => {
    if (user.credits <= 0) {
      if (onError) onError('Balance zero. Please purchase credits from the Credit Store before launching.');
      return;
    }

    if (!jobTitle.trim() || !company.trim()) {
      if (onError) onError('Please enter a valid Job Title and Target Company name.');
      return;
    }

    setLaunchStatus('Initiating session handshake...');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/interviews/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          category: selectedType,
          model: selectedModel,
          jobTitle: jobTitle,
          company: company,
          experienceLevel: experienceLevel,
          resumeText: resume
        })
      });

      const data = await response.json();
      if (data.success && (data.sessionId || data.interview?.sessionId)) {
        const sessionId = data.sessionId || data.interview?.sessionId;
        const interviewToken = data.interviewToken || data.interview?.interviewToken;

        setLaunchStatus('Launching CrackIT Desktop App...');

        const newSession = {
          id: sessionId,
          category: selectedType,
          status: 'active',
          modelName: models.find(m => m.id === selectedModel)?.name || 'GPT-4o Omniverse',
          timeSpent: '0 min',
          creditsSpent: '1 credit',
          timestamp: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          resume: resume || `${jobTitle} @ ${company}`,
          transcriptions: []
        };

        if (setPastInterviews) {
          setPastInterviews(prev => [newSession, ...prev]);
        }

        if (onUpdateUser && user) {
          onUpdateUser({ ...user, credits: Math.max(0, user.credits - 1) });
        }

        const deepLink = `crackit://launch?sessionId=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(interviewToken || '')}&authToken=${encodeURIComponent(token)}&credits=${Math.max(0, (user?.credits || 1) - 1)}&type=${encodeURIComponent(selectedType)}&model=${encodeURIComponent(selectedModel)}&resume=${encodeURIComponent(resume || '')}`;

        // 1. Direct local HTTP sync backup (instantaneous local desktop connection)
        try {
          fetch('http://127.0.0.1:4312', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            mode: 'no-cors',
            body: JSON.stringify({
              sessionId: sessionId,
              token: interviewToken,
              authToken: token,
              type: selectedType,
              resume: resume,
              model: selectedModel
            })
          }).catch(() => console.log('Local sync server offline'));
        } catch (err) {}

        // 2. Trigger OS protocol deep link to Electron desktop client
        try {
          window.location.href = deepLink;
        } catch (err) {
          console.error('Deep link launch failed:', err);
        }

        if (onSuccess) onSuccess('Interview Assistant launched! Check your desktop overlay.');
        setTimeout(() => {
          setLaunchStatus('');
          setCurrentTab('history');
          navigate('/dashboard/history');
        }, 1200);
      } else {
        if (onError) onError('Failed to start session: ' + (data.message || 'Unknown error'));
        setLaunchStatus('');
      }
    } catch (err) {
      console.error('Error starting session:', err);
      if (onError) onError('Backend communication error.');
      setLaunchStatus('');
    }
  };

  // PDF Resume Upload Handler
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
      if (onError) onError('Please upload a valid PDF document.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      if (onError) onError('File size exceeds the 5MB limit.');
      return;
    }

    try {
      setPdfLoading(true);
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64String = event.target.result.split(',')[1];
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/interviews/parse-pdf`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ fileBase64: base64String })
          });
          const data = await response.json();
          if (data.success && data.text) {
            setResume(data.text);
            if (onSuccess) onSuccess('PDF resume parsed successfully!');
          } else {
            if (onError) onError('Failed to parse PDF: ' + (data.message || 'Unknown error'));
          }
        } catch (err) {
          console.error(err);
          if (onError) onError('PDF parse connection failed.');
        } finally {
          setPdfLoading(false);
        }
      };
      reader.onerror = () => {
        if (onError) onError('Failed to read PDF file.');
        setPdfLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      if (onError) onError('Unexpected error uploading file.');
      setPdfLoading(false);
    }
  };

  // End Interview Session Handler
  const executeEndInterview = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      
      try {
        fetch('http://127.0.0.1:4312', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          mode: 'no-cors',
          body: JSON.stringify({
            type: 'session_completed',
            action: 'complete_session',
            sessionId: sessionId
          })
        }).catch(() => console.log('Local sync server offline'));
      } catch (err) {}

      const response = await fetch(`${API_URL}/api/interviews/${sessionId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        if (onSuccess) onSuccess('Interview ended successfully.');
        if (onUpdateUser && user) {
          onUpdateUser({ ...user, credits: data.remainingTokens });
        }
        if (setPastInterviews) {
          setPastInterviews(prev => prev.map(s => {
            if (s.id === sessionId) {
              return { ...s, status: 'completed' };
            }
            return s;
          }));
        }
      } else {
        if (onError) onError('Failed to end interview: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error ending interview:', err);
      if (onError) onError('Error connecting to backend to end interview.');
    }
  };

  // Toggle Sidebar Accordion
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // User Greeting Info
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getUserDisplayName = () => {
    if (!user) return 'Candidate';
    if (user.name) return user.name.split(' ')[0].toUpperCase();
    if (user.email) return user.email.split('@')[0].toUpperCase();
    return 'CANDIDATE';
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.name) {
      const parts = user.name.split(' ');
      return parts.length > 1 ? (parts[0][0] + parts[1][0]).toUpperCase() : parts[0].slice(0, 2).toUpperCase();
    }
    if (user.email) return user.email.slice(0, 2).toUpperCase();
    return 'U';
  };

  // Fetch individual session details when viewing `/session/:id` to retrieve uncompressed screenshots
  useEffect(() => {
    if (currentTab === 'detail' && id) {
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${API_URL}/api/interviews/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(r => r.json())
          .then(data => {
            if (data.success && data.interview) {
              setDetailInterview(data.interview);
            }
          })
          .catch(err => console.error('Error fetching detail interview:', err));
      }
    } else {
      setDetailInterview(null);
    }
  }, [currentTab, id]);

  // Active Session details for `/session/:id`
  const sessionBase = currentTab === 'detail' && pastInterviews
    ? pastInterviews.find(s => s.id === id)
    : null;

  const activeSessionDetails = sessionBase ? {
    ...sessionBase,
    transcriptions: detailInterview?.transcriptions?.length > 0
      ? detailInterview.transcriptions.map(t => {
          const timeStr = t.timestamp ? new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '00:00';
          return [
            {
              speaker: 'Interviewer',
              time: timeStr,
              text: t.content,
              screenshot: t.screenshot || null,
              screenshots: t.screenshots || (t.screenshot ? [t.screenshot] : [])
            },
            ...(t.answer && t.answer.content ? [{
              speaker: 'Copilot AI',
              time: t.answer.timestamp ? new Date(t.answer.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : timeStr,
              text: t.answer.content
            }] : [])
          ];
        }).flat()
      : sessionBase.transcriptions
  } : null;

  // Weekly activity simulation / calculations
  const weeklyDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weeklyUsage = [0, 1, 0, 2, 0, 0, 1];
  const totalWeeklyCredits = pastInterviews?.length || 0;

  return (
    <div className={`coprep-dashboard-app ${themeMode === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      
      {/* ====================================================================
          1. LEFT SIDEBAR (CoPrep AI Layout - Clean Vector Icons)
         ==================================================================== */}
      <aside className={`coprep-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
        
        {/* Sidebar Brand Header */}
        <div className="sidebar-top-brand">
          <div className="sidebar-brand-link" onClick={() => { setCurrentTab('overview'); navigate('/dashboard'); }}>
            <div className="brand-logo-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <span className="brand-logo-title">
              CRACKIT<span className="brand-ai-badge">AI</span>
            </span>
          </div>
        </div>

        {/* Sidebar Scrollable Nav Menu */}
        <div className="sidebar-menu-scrollable">
          
          {/* Main Dashboard Button */}
          <button 
            className={`sidebar-nav-btn ${currentTab === 'overview' ? 'active-nav-item' : ''}`}
            onClick={() => {
              setCurrentTab('overview');
              navigate('/dashboard');
              setMobileSidebarOpen(false);
            }}
          >
            <span className="nav-btn-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="7" height="9" x="3" y="3" rx="1"/>
                <rect width="7" height="5" x="14" y="3" rx="1"/>
                <rect width="7" height="9" x="14" y="12" rx="1"/>
                <rect width="7" height="5" x="3" y="16" rx="1"/>
              </svg>
            </span>
            <span className="nav-btn-text">Dashboard</span>
          </button>

          {/* Group 1: Interview */}
          <div className="sidebar-group-item">
            <button 
              className="sidebar-group-toggle"
              onClick={() => toggleGroup('interview')}
            >
              <div className="group-toggle-left">
                <span className="group-toggle-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m22 8-6 4 6 4V8Z"/>
                    <rect width="14" height="12" x="2" y="6" rx="2"/>
                  </svg>
                </span>
                <span className="group-toggle-text">Interview</span>
              </div>
              <div className="group-toggle-right">
                <span className={`toggle-chevron ${expandedGroups.interview ? 'expanded' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </div>
            </button>

            {expandedGroups.interview && (
              <div className="sidebar-sub-menu">
                <button 
                  className={`sidebar-sub-item ${currentTab === 'setup' ? 'active-sub-item' : ''}`}
                  onClick={() => {
                    setCurrentTab('setup');
                    navigate('/setup');
                    setMobileSidebarOpen(false);
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 8V4H8"/>
                      <rect width="16" height="12" x="4" y="8" rx="2"/>
                      <path d="M2 14h2"/>
                      <path d="M20 14h2"/>
                      <path d="M15 13v2"/>
                      <path d="M9 13v2"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Interview Assistant</span>
                </button>

                <button 
                  className="sidebar-sub-item disabled-sub-item"
                  onClick={() => {
                    if (onError) onError('AI Mock Interviews with real-time feedback are Coming Soon!');
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                      <line x1="12" y1="19" x2="12" y2="22"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Mock Interview</span>
                  <span className="coming-soon-pill">Soon</span>
                </button>

                <button 
                  className={`sidebar-sub-item ${currentTab === 'history' ? 'active-sub-item' : ''}`}
                  onClick={() => {
                    setCurrentTab('history');
                    navigate('/dashboard/history');
                    setMobileSidebarOpen(false);
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Interview Sessions</span>
                  {pastInterviews.length > 0 && (
                    <span className="session-count-badge">{pastInterviews.length}</span>
                  )}
                </button>

                <button 
                  className="sidebar-sub-item disabled-sub-item"
                  onClick={() => {
                    if (onError) onError('Question Bank with 5,000+ technical & behavioral questions is Coming Soon!');
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <ellipse cx="12" cy="5" rx="9" ry="3"/>
                      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Question Bank</span>
                  <span className="coming-soon-pill">Soon</span>
                </button>
              </div>
            )}
          </div>

          {/* Group 2: Resume & Profile */}
          <div className="sidebar-group-item">
            <button 
              className="sidebar-group-toggle"
              onClick={() => toggleGroup('resume')}
            >
              <div className="group-toggle-left">
                <span className="group-toggle-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="10" r="3"/>
                    <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"/>
                  </svg>
                </span>
                <span className="group-toggle-text">Resume & Profile</span>
              </div>
              <div className="group-toggle-right">
                <span className={`toggle-chevron ${expandedGroups.resume ? 'expanded' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </div>
            </button>

            {expandedGroups.resume && (
              <div className="sidebar-sub-menu">
                <button 
                  className="sidebar-sub-item disabled-sub-item"
                  onClick={() => onError && onError('ATS Resume Score analyzer is Coming Soon!')}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">ATS Resume Score</span>
                  <span className="coming-soon-pill">Soon</span>
                </button>
                <button 
                  className="sidebar-sub-item disabled-sub-item"
                  onClick={() => onError && onError('AI Bullet Builder is Coming Soon!')}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">AI Bullet Builder</span>
                  <span className="coming-soon-pill">Soon</span>
                </button>
              </div>
            )}
          </div>

          {/* Group 3: Job Search */}
          <div className="sidebar-group-item">
            <button 
              className="sidebar-group-toggle"
              onClick={() => toggleGroup('jobsearch')}
            >
              <div className="group-toggle-left">
                <span className="group-toggle-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                    <rect width="20" height="14" x="2" y="6" rx="2"/>
                  </svg>
                </span>
                <span className="group-toggle-text">Job Search</span>
              </div>
              <div className="group-toggle-right">
                <span className={`toggle-chevron ${expandedGroups.jobsearch ? 'expanded' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </div>
            </button>

            {expandedGroups.jobsearch && (
              <div className="sidebar-sub-menu">
                <button 
                  className="sidebar-sub-item disabled-sub-item"
                  onClick={() => onError && onError('Job Board Matching & Tracking is Coming Soon!')}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Job Match & Tracker</span>
                  <span className="coming-soon-pill">Soon</span>
                </button>
              </div>
            )}
          </div>

          {/* Group 4: Resources */}
          <div className="sidebar-group-item">
            <button 
              className="sidebar-group-toggle"
              onClick={() => toggleGroup('resources')}
            >
              <div className="group-toggle-left">
                <span className="group-toggle-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z"/>
                    <path d="M6 6h10"/>
                    <path d="M6 10h10"/>
                  </svg>
                </span>
                <span className="group-toggle-text">Resources</span>
              </div>
              <div className="group-toggle-right">
                <span className={`toggle-chevron ${expandedGroups.resources ? 'expanded' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </div>
            </button>

            {expandedGroups.resources && (
              <div className="sidebar-sub-menu">
                <button 
                  className="sidebar-sub-item"
                  onClick={() => setShowTipModal(true)}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
                      <path d="M9 18h6"/>
                      <path d="M10 22h4"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Interview Best Practices</span>
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Sidebar Bottom Cards Container */}
        <div className="sidebar-bottom-container">
          
          {/* Credits & Subscription Card */}
          <div className="sidebar-credits-card">
            <div className="credits-card-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect width="20" height="14" x="2" y="5" rx="2"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
              <span className="credits-card-title">Credits & Subscription</span>
            </div>
            <p className="credits-card-desc">
              Unlock longer sessions with credits, or go unlimited with a plan.
            </p>
            <button 
              className="credits-view-plans-btn"
              onClick={() => {
                setCurrentTab('store');
                navigate('/store');
                setMobileSidebarOpen(false);
              }}
            >
              View Plans
            </button>
          </div>

          {/* Desktop App Link */}
          <button 
            className="sidebar-desktop-app-btn"
            onClick={() => setShowDesktopModal(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect width="20" height="14" x="2" y="3" rx="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            <span>Desktop App</span>
          </button>

          {/* User Profile Pill with 3-dots */}
          <div className="sidebar-user-pill" ref={profileMenuRef}>
            <div className="user-avatar-circle">
              {getUserInitials()}
            </div>
            <div className="user-pill-info">
              <span className="user-pill-name">{getUserDisplayName()}</span>
              <span className="user-pill-email" title={user?.email}>{user?.email || 'user@email.com'}</span>
            </div>
            <button 
              className="user-pill-more-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              aria-label="User menu"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="1.5"/>
                <circle cx="12" cy="5" r="1.5"/>
                <circle cx="12" cy="19" r="1.5"/>
              </svg>
            </button>

            {/* User Dropdown Options */}
            {showProfileMenu && (
              <div className="sidebar-user-menu-dropdown">
                <div className="user-menu-header">
                  <span className="menu-user-credits">Balance: {Math.round(user?.credits || 0).toLocaleString()} Credits</span>
                </div>
                <button 
                  className="user-menu-item"
                  onClick={() => {
                    setCurrentTab('store');
                    setShowProfileMenu(false);
                  }}
                >
                  Refill Credits
                </button>
                <button 
                  className="user-menu-item"
                  onClick={() => {
                    navigate('/');
                    setShowProfileMenu(false);
                  }}
                >
                  Main Website
                </button>
                {user?.role === 'admin' && (
                  <button 
                    className="user-menu-item"
                    onClick={() => {
                      navigate('/admin');
                      setShowProfileMenu(false);
                    }}
                    style={{ color: '#ef4444', fontWeight: 700 }}
                  >
                    Admin Control Panel
                  </button>
                )}
                <div className="user-menu-divider"></div>
                <button 
                  className="user-menu-item text-danger"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>

        </div>

      </aside>

      {/* Mobile Backdrop */}
      {mobileSidebarOpen && (
        <div 
          className="sidebar-mobile-backdrop"
          onClick={() => setMobileSidebarOpen(false)}
        ></div>
      )}

      {/* ====================================================================
          2. MAIN CONTENT STAGE (Header + Active View)
         ==================================================================== */}
      <main className="coprep-main-stage">
        
        {/* Top Navbar */}
        <header className="coprep-dashboard-topbar">
          <div className="topbar-left">
            <button 
              className="topbar-hamburger-btn"
              onClick={() => {
                if (window.innerWidth <= 900) {
                  setMobileSidebarOpen(!mobileSidebarOpen);
                } else {
                  setSidebarCollapsed(!sidebarCollapsed);
                }
              }}
              title="Toggle Menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="topbar-right">
            {/* Credits pill */}
            <div 
              className="topbar-credits-chip"
              onClick={() => setCurrentTab('store')}
              title="Click to refill credits"
            >
              <span className="chip-bolt">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              </span>
              <span className="chip-val">{Math.round(user?.credits || 0).toLocaleString()} Credits</span>
            </div>

            {/* Theme Switcher */}
            <button 
              className="topbar-icon-btn"
              onClick={handleToggleTheme}
              title={`Switch to ${themeMode === 'light' ? 'Dark' : 'Light'} Mode`}
            >
              {themeMode === 'light' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="4"/>
                  <path d="M12 2v2"/>
                  <path d="M12 20v2"/>
                  <path d="m4.93 4.93 1.41 1.41"/>
                  <path d="m17.66 17.66 1.41 1.41"/>
                  <path d="M2 12h2"/>
                  <path d="M20 12h2"/>
                  <path d="m6.34 17.66-1.41 1.41"/>
                  <path d="m19.07 4.93-1.41 1.41"/>
                </svg>
              )}
            </button>

            {/* Help / Guide */}
            <button 
              className="topbar-icon-btn"
              onClick={() => setShowTipModal(true)}
              title="Interview Tips & Help"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </button>
          </div>
        </header>

        {/* Dynamic Viewport Content */}
        <div className="dashboard-content-viewport">
          
          {/* ================================================================
              VIEW 1: OVERVIEW DASHBOARD (Exact replica of CoPrep screenshot)
             ================================================================ */}
          {currentTab === 'overview' && (
            <div className="dashboard-overview-container">
              
              {/* 1. Welcome Greeting Hero Card */}
              <motion.div 
                className="dashboard-greeting-card"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="greeting-text-area">
                  <h1 className="greeting-main-title">
                    {getGreeting()}, <span className="greeting-username">{getUserDisplayName()}</span>!
                  </h1>
                  <p className="greeting-subtitle">
                    Ready to ace your next interview? Let's continue your journey.
                  </p>
                </div>

                <button 
                  className="start-assistant-primary-btn"
                  onClick={() => {
                    setCurrentTab('setup');
                    navigate('/setup');
                  }}
                >
                  <span>Start Interview Assistant</span>
                  <span className="btn-arrow">›</span>
                </button>
              </motion.div>

              {/* 2. 4 Metrics Cards Row (Black Vector Outline SVGs) */}
              <div className="dashboard-metrics-grid">
                
                {/* Metric 1: Sessions */}
                <div 
                  className="metric-card-tile clickable-metric"
                  onClick={() => {
                    setCurrentTab('history');
                    navigate('/dashboard/history');
                  }}
                >
                  <div className="metric-header-row">
                    <div className="metric-icon-badge badge-green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <circle cx="12" cy="12" r="6"/>
                        <circle cx="12" cy="12" r="2"/>
                      </svg>
                    </div>
                    <span className="metric-tag-label">Sessions</span>
                  </div>
                  <h3 className="metric-primary-val">{pastInterviews.length} Sessions</h3>
                  <span className="metric-status-link link-green">
                    {pastInterviews.length === 0 ? 'Start your first session' : 'View all sessions'}
                  </span>
                </div>

                {/* Metric 2: Resume */}
                <div 
                  className="metric-card-tile clickable-metric"
                  onClick={() => {
                    setCurrentTab('setup');
                    navigate('/setup');
                  }}
                >
                  <div className="metric-header-row">
                    <div className="metric-icon-badge badge-purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                    </div>
                    <span className="metric-tag-label">Resume</span>
                  </div>
                  <h3 className="metric-primary-val">{resume ? 'Resume Attached' : 'No Score Yet'}</h3>
                  <span className="metric-status-link link-purple">
                    {resume ? 'Ready for interview' : 'Needs Improvement'}
                  </span>
                </div>

                {/* Metric 3: LinkedIn */}
                <div 
                  className="metric-card-tile coming-soon-metric"
                  onClick={() => onError && onError('LinkedIn Profile Optimizer is Coming Soon!')}
                >
                  <div className="metric-header-row">
                    <div className="metric-icon-badge badge-cyan">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
                        <polyline points="16 7 22 7 22 13"/>
                      </svg>
                    </div>
                    <div className="metric-tag-right">
                      <span className="metric-tag-label">LinkedIn</span>
                      <span className="coming-soon-pill">Soon</span>
                    </div>
                  </div>
                  <h3 className="metric-primary-val">Coming Soon</h3>
                  <span className="metric-status-link link-cyan">Profile Optimizer (Soon)</span>
                </div>

                {/* Metric 4: Headshots */}
                <div 
                  className="metric-card-tile coming-soon-metric"
                  onClick={() => onError && onError('AI Professional Headshots Generator is Coming Soon!')}
                >
                  <div className="metric-header-row">
                    <div className="metric-icon-badge badge-pink">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="6"/>
                        <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/>
                      </svg>
                    </div>
                    <div className="metric-tag-right">
                      <span className="metric-tag-label">Headshots</span>
                      <span className="coming-soon-pill">Soon</span>
                    </div>
                  </div>
                  <h3 className="metric-primary-val">Coming Soon</h3>
                  <span className="metric-status-link link-pink">AI Headshots (Soon)</span>
                </div>

              </div>

              {/* 3. Fuel Your Copilot Banner - Only when credits < 100 */}
              {(user?.credits || 0) < 100 && (
                <div className="fuel-copilot-banner-card">
                  <div className="fuel-text-block">
                    <h4 className="fuel-title">Fuel Your Copilot</h4>
                    <p className="fuel-desc">
                      You have only {Math.round(user?.credits || 0).toLocaleString()} credits left. Buy more or go unlimited to continue using without interruptions.
                    </p>
                  </div>
                  <button 
                    className="fuel-view-plans-btn"
                    onClick={() => {
                      setCurrentTab('store');
                      navigate('/store');
                    }}
                  >
                    View Plans
                  </button>
                </div>
              )}

              {/* 4. Bottom 2-Column Grid (Weekly Activity + Tip of the Day) */}
              <div className="dashboard-bottom-grid">
                
                {/* Left Column: Weekly Activity */}
                <div className="dashboard-sub-card weekly-activity-card">
                  <div className="card-top-row">
                    <div className="card-title-block">
                      <h4 className="card-heading">Weekly Activity</h4>
                      <span className="card-subheading">Credits used in the last 7 days</span>
                    </div>
                    <div className="card-right-metric">
                      <span className="big-num">{totalWeeklyCredits}</span>
                      <span className="lbl">Total Credits Used</span>
                    </div>
                  </div>

                  {/* Chart Visualizer */}
                  <div className="weekly-chart-area">
                    <div className="chart-gridlines">
                      <div className="gridline-row"><span>4</span><div className="line"></div></div>
                      <div className="gridline-row"><span>3</span><div className="line"></div></div>
                      <div className="gridline-row"><span>2</span><div className="line"></div></div>
                      <div className="gridline-row"><span>1</span><div className="line"></div></div>
                      <div className="gridline-row"><span>0</span><div className="line"></div></div>
                    </div>

                    <div className="chart-bars-row">
                      {weeklyDays.map((day, idx) => (
                        <div key={day} className="chart-column-item">
                          <div className="bar-track">
                            <div 
                              className="bar-fill" 
                              style={{ 
                                height: `${weeklyUsage[idx] * 25}%`,
                                background: weeklyUsage[idx] > 0 ? 'linear-gradient(180deg, #00f0ff 0%, #0284c7 100%)' : 'rgba(255, 255, 255, 0.05)',
                                boxShadow: weeklyUsage[idx] > 0 ? '0 0 12px rgba(0, 240, 255, 0.45)' : 'none'
                              }}
                            ></div>
                          </div>
                          <span className="day-label">{day}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column: Tip of the Day */}
                <div className="dashboard-sub-card tip-of-day-card">
                  <div className="tip-badge-header">
                    <span className="bulb-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
                        <path d="M9 18h6"/>
                        <path d="M10 22h4"/>
                      </svg>
                    </span>
                    <span className="tip-header-text">Tip of the Day</span>
                  </div>

                  <h3 className="tip-article-title">Remote Interview Best Practices</h3>
                  <p className="tip-article-excerpt">
                    Tips for succeeding in virtual interviews and avoiding common communication pitfalls.
                  </p>

                  <div className="tip-card-footer">
                    <span className="read-time">1 min read</span>
                    <button 
                      className="read-more-link"
                      onClick={() => setShowTipModal(true)}
                    >
                      Read More ›
                    </button>
                  </div>
                </div>

              </div>

            </div>
          )}

          {/* ================================================================
              VIEW 2: INTERVIEW SETUP / ASSISTANT (Tab === 'setup')
             ================================================================ */}
          {currentTab === 'setup' && (
            <div className="dashboard-setup-container">
              <div className="setup-view-header">
                <div>
                  <h2 className="setup-heading">Setup Your Interview</h2>
                  <p className="setup-subheading">Configure your session parameters before launching the desktop overlay.</p>
                </div>
              </div>

              <div className="setup-form-card">
                <div className="setup-two-col-grid">
                  
                  {/* Left Column: Role Details */}
                  <div className="setup-form-left">
                    <div className="form-group-item">
                      <label className="input-label">1. Target Job Title</label>
                      <input 
                        type="text" 
                        className="modern-text-input" 
                        placeholder="e.g. Senior Software Engineer"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                      />
                    </div>

                    <div className="form-group-item">
                      <label className="input-label">2. Target Company Name</label>
                      <input 
                        type="text" 
                        className="modern-text-input" 
                        placeholder="e.g. Stripe, Google, Amazon"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                      />
                    </div>

                    <div className="form-group-item">
                      <label className="input-label">3. Experience Level</label>
                      <select 
                        className="modern-text-input modern-select"
                        value={experienceLevel}
                        onChange={(e) => setExperienceLevel(e.target.value)}
                      >
                        <option value="entry">Entry Level / Junior (0-2 years)</option>
                        <option value="mid">Mid Level / Intermediate (3-5 years)</option>
                        <option value="senior">Senior Level (5+ years)</option>
                        <option value="lead">Staff / Lead / Principal Architect</option>
                      </select>
                    </div>

                    <div className="form-group-item">
                      <label className="input-label">4. Interview Category</label>
                      <div className="interview-category-pills">
                        {[
                          { 
                            id: 'technical', 
                            label: 'Technical Coding', 
                            icon: (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="16 18 22 12 16 6"/>
                                <polyline points="8 6 2 12 8 18"/>
                              </svg>
                            ) 
                          },
                          { 
                            id: 'behavioral', 
                            label: 'Behavioral & Leadership', 
                            icon: (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                              </svg>
                            ) 
                          },
                          { 
                            id: 'hr', 
                            label: 'HR Screening', 
                            icon: (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <polyline points="16 11 18 13 22 9"/>
                              </svg>
                            ) 
                          },
                          { 
                            id: 'negotiation', 
                            label: 'Offer Negotiation', 
                            icon: (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect width="20" height="14" x="2" y="7" rx="2" ry="2"/>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                              </svg>
                            ) 
                          }
                        ].map(type => (
                          <div 
                            key={type.id}
                            className={`category-pill-card ${selectedType === type.id ? 'active-pill' : ''}`}
                            onClick={() => setSelectedType(type.id)}
                          >
                            <span className="pill-icon">{type.icon}</span>
                            <span className="pill-label">{type.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: AI Model & Resume Upload */}
                  <div className="setup-form-right">
                    
                    <div className="form-group-item">
                      <label className="input-label">5. AI Intelligence Model</label>
                      <div className="models-selection-list">
                        {models.map(model => (
                          <div 
                            key={model.id}
                            className={`model-option-card ${selectedModel === model.id ? 'active-model' : ''} ${model.developer === 'Coming Soon' ? 'disabled-model' : ''}`}
                            onClick={() => {
                              if (model.developer === 'Coming Soon') {
                                if (onError) onError('Gemini and Claude models are Coming Soon!');
                                return;
                              }
                              setSelectedModel(model.id);
                            }}
                          >
                            <div className="model-option-header">
                              <span className="model-icon-str">
                                {model.type === 'chip' ? (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect width="16" height="16" x="4" y="4" rx="2"/>
                                    <rect width="6" height="6" x="9" y="9" rx="1"/>
                                    <path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/>
                                  </svg>
                                ) : model.type === 'brain' ? (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-5.04Z"/>
                                    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-5.04Z"/>
                                  </svg>
                                ) : (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/>
                                  </svg>
                                )}
                              </span>
                              <div className="model-name-box">
                                <span className="model-title-str">{model.name}</span>
                                {model.developer && (
                                  <span className={`model-tag-pill ${model.developer === 'Recommended' ? 'tag-recommended' : 'tag-soon'}`}>
                                    {model.developer}
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="model-desc-str">{model.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* PDF Resume Parser Dropzone */}
                    <div className="form-group-item">
                      <label className="input-label">6. Target Resume (Optional Context)</label>
                      <div className="resume-dropzone-box">
                        <input 
                          type="file" 
                          id="pdfResumeInput"
                          accept=".pdf" 
                          style={{ display: 'none' }}
                          onChange={handlePdfUpload}
                        />
                        <button 
                          type="button" 
                          className="pdf-upload-btn"
                          onClick={() => document.getElementById('pdfResumeInput').click()}
                          disabled={pdfLoading}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                          {pdfLoading ? 'Parsing PDF...' : 'Upload PDF Resume (Auto-Parse)'}
                        </button>

                        <textarea 
                          className="resume-raw-textarea"
                          rows="4"
                          placeholder="Or paste your resume experience / key bullet points here..."
                          value={resume}
                          onChange={(e) => setResume(e.target.value)}
                        />
                      </div>
                    </div>

                  </div>

                </div>

                {/* Launch Action Footer */}
                <div className="setup-launch-footer">
                  <div className="launch-credits-hint">
                    <span>Cost: <strong>1 Credit / min</strong> of live AI audio & screen OCR</span>
                    <span>• Balance: <strong>{Math.round(user?.credits || 0).toLocaleString()} Credits</strong></span>
                  </div>

                  <button 
                    className="launch-copilot-submit-btn"
                    onClick={handleLaunch}
                    disabled={!!launchStatus}
                  >
                    <span>{launchStatus || 'Launch Live Interview Copilot'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================
              VIEW 3: INTERVIEW SESSIONS / HISTORY (Tab === 'history')
             ================================================================ */}
          {currentTab === 'history' && (
            <div className="dashboard-history-container">
              <div className="history-view-header">
                <div>
                  <h2 className="setup-heading">Interview Sessions</h2>
                  <p className="setup-subheading">Review your past interview questions, AI suggestions, and recordings.</p>
                </div>

                <button 
                  className="start-new-session-top-btn"
                  onClick={() => {
                    setCurrentTab('setup');
                    navigate('/setup');
                  }}
                >
                  + Start New Interview
                </button>
              </div>

              {!pastInterviews || pastInterviews.length === 0 ? (
                <div className="empty-sessions-state">
                  <span className="empty-icon">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </span>
                  <h3>No Interview Sessions Found</h3>
                  <p>You haven't run any AI interview copilot sessions yet. Configure your first interview session to get started.</p>
                  <button 
                    className="start-assistant-primary-btn"
                    onClick={() => {
                      setCurrentTab('setup');
                      navigate('/setup');
                    }}
                  >
                    Setup Your First Session ›
                  </button>
                </div>
              ) : (() => {
                const historyPerPage = 12;
                const totalHistoryPages = Math.ceil(pastInterviews.length / historyPerPage) || 1;
                const safeHistoryPage = Math.min(historyPage, totalHistoryPages);
                const paginatedSessions = pastInterviews.slice((safeHistoryPage - 1) * historyPerPage, safeHistoryPage * historyPerPage);

                return (
                  <>
                    <div className="sessions-list-grid">
                      {paginatedSessions.map((session) => (
                        <div 
                          key={session.id}
                          className="session-record-card"
                          onClick={() => navigate(`/session/${session.id}`)}
                        >
                          <div className="record-header">
                            <div className="record-id-badge">
                              <span className="dot-live">●</span>
                              <span>{session.id}</span>
                            </div>
                            <span className={`status-pill pill-${session.status}`}>
                              {session.status.toUpperCase()}
                            </span>
                          </div>

                          <div className="record-body">
                            <span className="record-category-tag">
                              {session.category === 'technical' ? 'Technical Round' :
                               session.category === 'behavioral' ? 'Behavioral & Leadership' :
                               session.category === 'hr' ? 'HR Screen' : 'Negotiation'}
                            </span>
                            <p className="record-title-preview">
                              {session.resume && session.resume.length > 80 ? session.resume.slice(0, 80) + '...' : session.resume || 'Live Interview Session'}
                            </p>
                          </div>

                          <div className="record-footer">
                            <span className="record-date">{session.timestamp}</span>
                            {session.status === 'active' && (
                              <button 
                                className="record-end-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  executeEndInterview(session.id);
                                }}
                              >
                                End Session
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {totalHistoryPages > 1 && (
                      <div className="sessions-pagination-bar">
                        <button 
                          className="pagination-btn"
                          disabled={safeHistoryPage === 1}
                          onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                        >
                          ‹ Previous
                        </button>
                        <span className="pagination-info">
                          Page {safeHistoryPage} of {totalHistoryPages} ({pastInterviews.length} total sessions)
                        </span>
                        <button 
                          className="pagination-btn"
                          disabled={safeHistoryPage >= totalHistoryPages}
                          onClick={() => setHistoryPage(p => Math.min(totalHistoryPages, p + 1))}
                        >
                          Next ›
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* ================================================================
              VIEW 4: CREDITS & STORE (Tab === 'store')
             ================================================================ */}
          {currentTab === 'store' && (
            <div className="dashboard-store-container">
              <div className="store-view-header">
                <div>
                  <h2 className="setup-heading">Credit Store & Plans</h2>
                  <p className="setup-subheading">Refill credits with zero recurring fees. Instant activation via Razorpay.</p>
                </div>
              </div>

              <div className="store-pricing-grid">
                {pricingTiers.map((plan) => (
                  <div 
                    key={plan._id} 
                    className={`store-plan-card ${plan.isPopular || plan.badge ? 'popular-plan' : ''}`}
                  >
                    {(plan.badge || plan.isPopular) && (
                      <div className="popular-ribbon">{plan.badge || 'MOST POPULAR'}</div>
                    )}
                    
                    <h3 className="plan-name">{plan.name}</h3>
                    <p className="plan-desc">
                      {plan.description || `${plan.duration?.hours || Math.max(1, Math.floor(plan.credits / 60))} Hours of Live Interview Copilot (${Math.round(plan.credits || 0).toLocaleString()} Credits)`}
                    </p>
                    
                    <div className="plan-price-block">
                      <span className="currency">₹</span>
                      <span className="price-val">{plan.price}</span>
                      <span className="one-time">one-time</span>
                    </div>

                    <div className="plan-credits-callout">
                      <strong>{Math.round(plan.credits || 0).toLocaleString()} Copilot Credits</strong>
                    </div>

                    <ul className="plan-perks-list">
                      {plan.features && plan.features.length > 0 ? (
                        plan.features.map((f, i) => (
                          <li key={i}>
                            <span className="perk-check">✓</span>
                            <span>{typeof f === 'string' ? f.replace(/^[✓•\s]+/, '') : f}</span>
                          </li>
                        ))
                      ) : (
                        <>
                          <li><span className="perk-check">✓</span><span>{Math.round(plan.credits || 0).toLocaleString()} Live Copilot Credits</span></li>
                          <li><span className="perk-check">✓</span><span>100% Undetectable Screen Overlay</span></li>
                          <li><span className="perk-check">✓</span><span>Live Speech Recognition & AI Answers</span></li>
                          <li><span className="perk-check">✓</span><span>Never Expires & No Subscriptions</span></li>
                        </>
                      )}
                    </ul>

                    <button 
                      className={`plan-buy-btn ${plan.isPopular ? 'buy-btn-primary' : 'buy-btn-secondary'}`}
                      onClick={() => handlePurchase(plan._id)}
                      disabled={isPurchasing}
                    >
                      {isPurchasing && selectedTier === plan._id ? 'Processing...' : `Buy ${Math.round(plan.credits || 0).toLocaleString()} Credits`}
                    </button>
                  </div>
                ))}
              </div>

              <div className="store-refund-note">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>
                  <strong>Strict No-Refund Policy:</strong> All digital credit pack purchases are final, instant, and non-refundable. Read our full <a href="/refund-policy" target="_blank" rel="noreferrer">Refund Policy</a>.
                </span>
              </div>
            </div>
          )}

          {/* ================================================================
              VIEW 5: SESSION DETAIL VIEW (Tab === 'detail')
             ================================================================ */}
          {currentTab === 'detail' && activeSessionDetails && (
            <div className="dashboard-detail-container">
              <div className="detail-top-nav">
                <button 
                  className="detail-back-btn"
                  onClick={() => {
                    setCurrentTab('history');
                    navigate('/dashboard/history');
                  }}
                >
                  ← Back to Sessions
                </button>
                <div className="detail-session-tag">
                  <span>Session: {activeSessionDetails.id}</span>
                </div>
              </div>

              <div className="detail-stats-cards-grid">
                <div className="detail-stat-card">
                  <span className="stat-lbl">Status</span>
                  <span className={`stat-val status-${activeSessionDetails.status}`}>{activeSessionDetails.status.toUpperCase()}</span>
                </div>
                <div className="detail-stat-card">
                  <span className="stat-lbl">Category</span>
                  <span className="stat-val">{activeSessionDetails.category}</span>
                </div>
                <div className="detail-stat-card">
                  <span className="stat-lbl">Time Spent</span>
                  <span className="stat-val">{activeSessionDetails.timeSpent}</span>
                </div>
                <div className="detail-stat-card">
                  <span className="stat-lbl">Credits Deducted</span>
                  <span className="stat-val">{activeSessionDetails.creditsSpent}</span>
                </div>
              </div>

              {/* Transcription Log Feed */}
              <div className="detail-transcript-feed-card">
                <h3 className="feed-title">Real-Time Audio Transcription & Copilot Suggestions</h3>
                
                <div className="feed-items-list">
                  {activeSessionDetails.transcriptions && activeSessionDetails.transcriptions.length > 0 ? (
                    activeSessionDetails.transcriptions.map((log, idx) => {
                      const entryScreenshots = getEntryScreenshots(log);
                      return (
                        <div key={idx} className={`feed-bubble-row ${log.speaker === 'Interviewer' ? 'speaker-interviewer' : 'speaker-copilot'}`}>
                          <div className="bubble-speaker-header">
                            <span className="bubble-speaker-name">{log.speaker}</span>
                            <span className="bubble-speaker-time">{log.time}</span>
                          </div>
                          {log.speaker === 'Copilot AI' ? (
                            <div className="bubble-text" dangerouslySetInnerHTML={{ __html: renderMarkdown(log.text) }} />
                          ) : (
                            <p className="bubble-text">{log.text}</p>
                          )}

                          {log.speaker === 'Interviewer' && entryScreenshots.length > 0 && (
                            <div className="transcript-screenshots-attachment" style={{ marginTop: '12px' }}>
                              <div className="transcript-screenshots-grid">
                                {entryScreenshots.map((imgSrc, sIdx) => (
                                  <div 
                                    key={sIdx} 
                                    className="screenshot-thumbnail-card"
                                    onClick={() => setPreviewImage({
                                      src: imgSrc,
                                      index: sIdx + 1,
                                      total: entryScreenshots.length,
                                      title: `Session ${activeSessionDetails.id} • Screenshot #${sIdx + 1}`
                                    })}
                                  >
                                    <div className="thumbnail-aspect-box">
                                      <img src={imgSrc} alt={`Screenshot ${sIdx + 1}`} />
                                      <div className="screenshot-zoom-overlay">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
                                          <circle cx="11" cy="11" r="8"/>
                                          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                          <line x1="11" y1="8" x2="11" y2="14"/>
                                          <line x1="8" y1="11" x2="14" y2="11"/>
                                        </svg>
                                        <span>Click to Expand</span>
                                      </div>
                                    </div>
                                    <div className="thumbnail-footer-caption">
                                      <span>Snapshot #{sIdx + 1}</span>
                                      <span className="view-full-tag">High-Res</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="empty-feed-placeholder">
                      {activeSessionDetails.status === 'active' ? (
                        <span>Active connection docked. Listening for live audio...</span>
                      ) : (
                        <span>No audio transcripts recorded for this session.</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

      </main>

      {/* ====================================================================
          3. FLOATING FEEDBACK BUTTON & MODAL
         ==================================================================== */}
      <button 
        className="dashboard-floating-feedback-pill"
        onClick={() => setShowFeedbackModal(true)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <span>Feedback</span>
      </button>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="coprep-modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="coprep-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#00f0ff' }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                Send Us Your Feedback
              </h3>
              <button className="modal-close-btn" onClick={() => setShowFeedbackModal(false)} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>How can we make CrackIT AI better for your interviews? Let us know your thoughts or feature requests!</p>
              <textarea 
                className="modern-textarea"
                rows="5"
                placeholder="Write your feedback, bug report, or feature requests here..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
              />
              <span className="modal-hint-text">
                Your feedback goes directly to our core engineering team for rapid product upgrades.
              </span>
            </div>
            <div className="modal-footer">
              <button className="modal-secondary-btn" onClick={() => setShowFeedbackModal(false)}>Cancel</button>
              <button 
                className="modal-primary-btn"
                onClick={() => {
                  if (!feedbackText.trim()) return;
                  if (onSuccess) onSuccess('Thank you! Your feedback has been sent to our engineering team.');
                  setFeedbackText('');
                  setShowFeedbackModal(false);
                }}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop App Download Modal */}
      {showDesktopModal && (
        <div className="coprep-modal-overlay" onClick={() => setShowDesktopModal(false)}>
          <div className="coprep-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#00f0ff' }}>
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
                Download CrackIT Desktop Overlay
              </h3>
              <button className="modal-close-btn" onClick={() => setShowDesktopModal(false)} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>Install the undetectable hardware-protected desktop client for macOS & Windows:</p>
              
              <div className="terminal-install-box">
                <div className="terminal-header-row">
                  <div className="terminal-micro-dots">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                  <span className="terminal-lbl">macOS 1-Click Terminal Setup</span>
                </div>
                <div className="terminal-code-snippet">
                  <code>curl -fsSL https://crackit.ai/install-mac.sh | bash</code>
                  <button 
                    className="copy-code-btn"
                    onClick={() => {
                      navigator.clipboard.writeText('curl -fsSL https://crackit.ai/install-mac.sh | bash');
                      setCopiedTerminal(true);
                      setTimeout(() => setCopiedTerminal(false), 2000);
                      if (onSuccess) onSuccess('Copied terminal command to clipboard!');
                    }}
                  >
                    {copiedTerminal ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="direct-download-links-row">
                <a href={downloadUrls.mac} target="_blank" rel="noopener noreferrer" className="download-tile-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v10m0 0l4-4m-4 4L8 8"></path>
                    <path d="M20 16v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4"></path>
                  </svg>
                  <span>macOS Universal (.dmg)</span>
                </a>
                <a href={downloadUrls.windows} target="_blank" rel="noopener noreferrer" className="download-tile-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v10m0 0l4-4m-4 4L8 8"></path>
                    <path d="M20 16v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4"></path>
                  </svg>
                  <span>Windows DirectML (.exe)</span>
                </a>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-primary-btn" onClick={() => setShowDesktopModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Tip of the Day Reader Modal */}
      {showTipModal && (
        <div className="coprep-modal-overlay" onClick={() => setShowTipModal(false)}>
          <div className="coprep-modal-card tip-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Remote Interview Best Practices</h3>
              <button className="modal-close-btn" onClick={() => setShowTipModal(false)} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body tip-modal-body">
              <div className="tip-item-card">
                <div className="tip-badge-num">01</div>
                <div className="tip-item-content">
                  <h4>Optimal Eye Level & Natural Pauses</h4>
                  <p>Position your webcam at eye level. When receiving a difficult algorithm or system design prompt, take a natural 3-second pause to structure your thoughts before speaking.</p>
                </div>
              </div>

              <div className="tip-item-card">
                <div className="tip-badge-num">02</div>
                <div className="tip-item-content">
                  <h4>The STAR Communication Technique</h4>
                  <p>For behavioral questions, structure your answers clearly: <strong>Situation</strong>, <strong>Task</strong>, <strong>Action</strong>, and <strong>Result</strong> with concrete metrics.</p>
                </div>
              </div>

              <div className="tip-item-card">
                <div className="tip-badge-num">03</div>
                <div className="tip-item-content">
                  <h4>Stealth Overlay Hotkeys</h4>
                  <p>Remember that you can toggle your CrackIT AI assistant HUD anytime during live screen sharing using <kbd>⌘ + Shift + H</kbd> (or <kbd>Ctrl + Shift + H</kbd> on Windows).</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-primary-btn" onClick={() => setShowTipModal(false)}>Got It</button>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Lightbox Overlay */}
      {previewImage && (
        <div 
          className="coprep-image-lightbox-overlay" 
          onClick={() => setPreviewImage(null)}
        >
          <div className="lightbox-content-box" onClick={e => e.stopPropagation()}>
            <div className="lightbox-header-bar">
              <div className="lightbox-title-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00f0ff" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span className="lightbox-title-text">{previewImage.title || 'Screen Capture'}</span>
              </div>
              <div className="lightbox-actions-group">
                <a 
                  href={previewImage.src} 
                  download={`crackit-capture-${Date.now()}.png`}
                  className="lightbox-btn-download"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  <span>Save Original</span>
                </a>
                <button 
                  className="modal-close-btn lightbox-btn-close" 
                  onClick={() => setPreviewImage(null)}
                  aria-label="Close Preview"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="lightbox-image-stage">
              <img 
                src={previewImage.src} 
                alt="Full screen capture inspection" 
                className="lightbox-full-img" 
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
